import React, { useEffect, useRef, useState } from "react";
import { Bell, ClipboardCheck, FileClock, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/vda";

const TYPE_ICON = { weekly_check_digest: ClipboardCheck, compliance_reminder: FileClock };
const SEV_DOT = { urgent: "hsl(var(--vda-red))", warning: "hsl(var(--vda-amber))", info: "hsl(var(--vda-green))" };

function useAlerts() {
  const [data, setData] = useState({ alerts: [], unread: 0 });
  const load = () => api.alertsList().then(setData).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  return { ...data, reload: load };
}

function AlertRow({ a, onRead }) {
  const Icon = TYPE_ICON[a.alert_type] || Bell;
  return (
    <div data-testid={`alert-${a.id}`} className={`flex gap-3 px-4 py-3 border-b border-border last:border-b-0 ${a.read_at ? "opacity-60" : ""}`}>
      <span className="mt-0.5 flex-none"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: SEV_DOT[a.severity] || SEV_DOT.info }} /></span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-sm font-semibold"><Icon className="h-3.5 w-3.5 text-muted-foreground" />{a.title}</div>
        <p className="text-xs text-muted-foreground mt-0.5">{a.body}</p>
        <p className="text-[10px] text-muted-foreground/70 mt-1 font-mono">{fmtDateTime(a.created_at)}</p>
      </div>
      {!a.read_at && <button data-testid={`alert-read-${a.id}`} onClick={() => onRead(a.id)} className="flex-none self-start text-muted-foreground hover:text-primary" title="Mark read"><Check className="h-4 w-4" /></button>}
    </div>
  );
}

export function AlertsBell() {
  const { alerts, unread, reload } = useAlerts();
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const markRead = async (id) => { await api.alertRead(id); reload(); };
  const markAll = async () => { await api.alertReadAll(); reload(); };
  return (
    <div className="relative" ref={ref}>
      <button data-testid="alerts-bell" onClick={() => setOpen((o) => !o)} className="relative p-2 hover:bg-secondary rounded-[6px] text-muted-foreground hover:text-foreground" title="Alerts">
        <Bell className="h-4 w-4" />
        {unread > 0 && <span data-testid="alerts-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 grid place-items-center rounded-full bg-[hsl(var(--vda-red))] text-white text-[10px] font-bold">{unread}</span>}
      </button>
      {open && (
        <div data-testid="alerts-dropdown" className="absolute right-0 mt-2 w-[340px] max-h-[420px] overflow-y-auto rounded-[8px] border border-border bg-card shadow-lg z-50">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border sticky top-0 bg-card">
            <span className="font-semibold text-sm">Alerts</span>
            <div className="flex items-center gap-2">
              {unread > 0 && <button data-testid="alerts-read-all" onClick={markAll} className="text-xs text-primary hover:underline">Mark all read</button>}
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
            </div>
          </div>
          {alerts.length === 0 ? <p className="px-4 py-6 text-sm text-muted-foreground text-center">No alerts yet.</p>
            : alerts.map((a) => <AlertRow key={a.id} a={a} onRead={markRead} />)}
        </div>
      )}
    </div>
  );
}

// Dashboard panel — shows unread alerts inline (FM + AM dashboards).
export function AlertsPanel() {
  const { alerts, reload } = useAlerts();
  const unread = alerts.filter((a) => !a.read_at);
  if (unread.length === 0) return null;
  const markRead = async (id) => { await api.alertRead(id); reload(); };
  return (
    <div className="mt-5 rounded-[8px] border border-border bg-card overflow-hidden" data-testid="alerts-panel">
      <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
        <Bell className="h-4 w-4 text-primary" /><span className="font-semibold">Alerts</span>
        <span className="font-mono text-sm text-muted-foreground">{unread.length}</span>
      </div>
      {unread.map((a) => <AlertRow key={a.id} a={a} onRead={markRead} />)}
    </div>
  );
}
